import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../context/AuthContext';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './CompetencyFramework.css';

const FALLBACK_LEVEL_NAMES = [
  'Entry Level',
  'Operational Excellence',
  'Functional Accountability',
  'Growth and Sustainability',
];

const CompetencyFramework = () => {
  const { organizationId } = useAuth();

  const [levelLabels, setLevelLabels]       = useState(FALLBACK_LEVEL_NAMES);
  const [categories, setCategories]         = useState([]);
  const [coreGroups, setCoreGroups]         = useState([]);
  const [deptData, setDeptData]             = useState([]);
  const [selectedDeptId, setSelectedDeptId] = useState(null);
  const [descriptorData, setDescriptorData]         = useState([]);
  const [funcDescriptorData, setFuncDescriptorData] = useState([]);
  const [loading, setLoading]               = useState(true);
  const [generating, setGenerating]         = useState(false);
  const [activeTab, setActiveTab]           = useState('A');

  const activeDept = deptData.find(d => d.department_id === selectedDeptId) || deptData[0] || null;

  useEffect(() => {
    if (!organizationId) return;

    const fetchAll = async () => {
      setLoading(true);

      const [catRes, coreRes, deptRes, funcRes, levelRes] = await Promise.all([
        supabase
          .from('competency_category')
          .select('category_id, category_name, category_weight')
          .eq('organization_id', organizationId)
          .order('created_at'),
        supabase
          .from('corp_competence_groups')
          .select('group_id, group_name, description, weighting, items:corp_competence_items(competency_id, competency_name)')
          .eq('organization_id', organizationId)
          .order('created_at'),
        supabase
          .from('department')
          .select('department_id, depart_name')
          .eq('organization_id', organizationId)
          .order('depart_name'),
        supabase
          .from('func_competence_groups')
          .select('group_id, group_name, description, group_weight, department_id, items:func_competence_items(item_id, item_name)')
          .eq('organization_id', organizationId)
          .order('created_at'),
        supabase
          .from('level_descriptions')
          .select('level_number, title')
          .order('level_number'),
      ]);

      const labels = (levelRes.data || [])
        .sort((a, b) => a.level_number - b.level_number)
        .map(r => r.title);
      if (labels.length === 4) setLevelLabels(labels);

      setCategories(catRes.data || []);

      const coreGroupsData = coreRes.data || [];
      setCoreGroups(coreGroupsData);

      const funcData = funcRes.data || [];
      const depts = (deptRes.data || [])
        .map(dept => ({
          ...dept,
          groups: funcData.filter(g => g.department_id === dept.department_id),
        }))
        .filter(d => d.groups.length > 0);
      setDeptData(depts);
      if (depts.length > 0) setSelectedDeptId(depts[0].department_id);

      // Collect competency IDs from the already-fetched corp and func data
      const coreCompIds = coreGroupsData.flatMap(g => (g.items || []).map(c => c.competency_id));
      const funcCompIds = funcData.flatMap(g => (g.items || []).map(c => c.item_id));
      const allDescIds  = [...new Set([...coreCompIds, ...funcCompIds])];

      if (allDescIds.length > 0) {
        const { data: descRows } = await supabase
          .from('competency_descriptions')
          .select('competency_id, description_level, description_name')
          .in('competency_id', allDescIds)
          .order('description_level');

        const descMap = {};
        (descRows || []).forEach(row => {
          if (!descMap[row.competency_id]) descMap[row.competency_id] = {};
          if (!descMap[row.competency_id][row.description_level]) {
            descMap[row.competency_id][row.description_level] = [];
          }
          descMap[row.competency_id][row.description_level].push(row.description_name);
        });

        // Core competency descriptors — built from corp_competence_groups/items
        const grouped = coreGroupsData
          .map(g => ({
            group_id:     g.group_id,
            group_name:   g.group_name,
            competencies: (g.items || []).map(c => ({
              competency_id:   c.competency_id,
              competency_name: c.competency_name,
              descriptors:     descMap[c.competency_id] || {},
            })),
          }))
          .filter(g => g.competencies.some(c => Object.keys(c.descriptors).length > 0));
        setDescriptorData(grouped);

        // Functional competency descriptors — built from func_competence_groups/items, grouped by dept
        const techDeptIds = [...new Set(funcData.map(g => g.department_id).filter(Boolean))];
        const funcGrouped = techDeptIds.map(deptId => {
          const deptInfo = (deptRes.data || []).find(d => d.department_id === deptId);
          return {
            department_id: deptId,
            depart_name:   deptInfo ? deptInfo.depart_name : String(deptId),
            groups: funcData
              .filter(g => g.department_id === deptId)
              .map(g => ({
                group_id:     g.group_id,
                group_name:   g.group_name,
                competencies: (g.items || [])
                  .map(c => ({
                    item_id:     c.item_id,
                    item_name:   c.item_name,
                    descriptors: descMap[c.item_id] || {},
                  }))
                  .filter(c => Object.keys(c.descriptors).length > 0),
              }))
              .filter(g => g.competencies.length > 0),
          };
        }).filter(d => d.groups.length > 0);
        setFuncDescriptorData(funcGrouped);
      }

      setLoading(false);
    };

    fetchAll();
  }, [organizationId]);


  // ── PDF generation ──────────────────────────────────────────────────────────
  const handleDownloadAll = () => {
    setGenerating(true);

    setTimeout(() => {
      // eslint-disable-next-line new-cap
      const doc  = new jsPDF('landscape', 'mm', 'a4');
      const PW   = 297;
      const PH   = 210;
      const ML   = 16;   // left margin
      const MR   = 16;   // right margin
      const CW   = PW - ML - MR;   // 178 mm usable
      const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });

      // ── Palette ──────────────────────────────────────────────────────────
      const G_DARK  = [0,  90,  66];   // #005a42 — headings / accents
      const G_MID   = [0, 107,  84];   // #006B54 — header fills
      const G_LIGHT = [232, 245, 240]; // light mint — alternate rows
      const WHITE   = [255, 255, 255];
      const DARK    = [30,  42,  38];
      const MUTED   = [100, 130, 120];
      const RULE    = [200, 225, 215]; // table border colour

      const sf = (...rgb) => doc.setFillColor(...rgb);
      const st = (...rgb) => doc.setTextColor(...rgb);
      const sd = (...rgb) => doc.setDrawColor(...rgb);

      // ── Shared table config ───────────────────────────────────────────────
      const headStyles    = { fillColor: G_DARK, textColor: WHITE, fontSize: 8.5, fontStyle: 'bold', cellPadding: { top: 5, bottom: 5, left: 6, right: 6 } };
      const bodyStyles    = { fontSize: 8.5, textColor: DARK, valign: 'top', cellPadding: { top: 5, bottom: 5, left: 6, right: 6 } };
      const altRowStyles  = { fillColor: G_LIGHT };
      const tableStyles   = { lineColor: RULE, lineWidth: 0.25, overflow: 'linebreak' };
      const tableMargin   = { left: ML, right: MR, bottom: 18 };

      // ── Running page header (slim bar — drawn on autoTable overflow pages) ─
      const runningHeader = () => {
        sf(...G_MID);
        doc.rect(0, 0, PW, 12, 'F');
        st(...WHITE);
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'bold');
        doc.text('TALENTSCOPE360', ML, 8);
        doc.setFont('helvetica', 'normal');
        doc.text('Competency Framework Report', ML + 36, 8);
        doc.text(today, PW - MR, 8, { align: 'right' });
      };

      // ── Section page (new page with a styled section header) ─────────────
      const sectionPage = (num, title, subtitle) => {
        doc.addPage();
        // Accent bar
        sf(...G_MID);
        doc.rect(0, 0, PW, 10, 'F');
        // Section number pill on the left
        sf(...G_DARK);
        doc.roundedRect(ML, 18, 10, 10, 2, 2, 'F');
        st(...WHITE);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(String(num), ML + 5, 25, { align: 'center' });
        // Section title
        st(...G_DARK);
        doc.setFontSize(17);
        doc.setFont('helvetica', 'bold');
        doc.text(title, ML + 14, 25);
        // Subtitle / date
        if (subtitle) {
          st(...MUTED);
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.text(subtitle, ML + 14, 32);
        }
        // Rule
        sd(...RULE);
        doc.setLineWidth(0.4);
        doc.line(ML, 36, PW - MR, 36);
        return 44;
      };

      // ── Department label bar (used inside section 3) ───────────────────────
      const deptBar = (name, y) => {
        sf(...G_LIGHT);
        sd(...RULE);
        doc.setLineWidth(0.3);
        doc.roundedRect(ML, y, CW, 8, 1, 1, 'FD');
        st(...G_DARK);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(name, ML + 5, y + 5.5);
        return y + 11;
      };

      // ── Group header bar (used inside section 4) ──────────────────────────
      const groupBar = (name, y) => {
        st(...G_DARK);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(name.toUpperCase(), ML, y);
        sd(...G_MID);
        doc.setLineWidth(0.5);
        doc.line(ML, y + 2, PW - MR, y + 2);
        return y + 8;
      };

      // ════════════════════════════════════════════════════════════════════════
      // COVER PAGE
      // ════════════════════════════════════════════════════════════════════════
      // Top brand stripe
      sf(...G_MID);
      doc.rect(0, 0, PW, 6, 'F');

      // Left accent column
      sf(...G_DARK);
      doc.rect(0, 6, 8, PH - 6, 'F');

      // Title block
      const TX = 20;
      st(...G_DARK);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('TALENTSCOPE360', TX, 28);

      doc.setFontSize(34);
      doc.text('Competency', TX, 55);
      doc.text('Framework', TX, 72);

      sd(...G_MID);
      doc.setLineWidth(1.2);
      doc.line(TX, 78, TX + 60, 78);

      st(...MUTED);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text('Full Compiled Report', TX, 89);
      doc.text(`Generated: ${today}`, TX, 97);

      // Table of contents box
      sf(248, 252, 250);
      sd(...RULE);
      doc.setLineWidth(0.3);
      doc.roundedRect(TX, 112, CW - 12, 58, 3, 3, 'FD');

      st(...G_DARK);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('TABLE OF CONTENTS', TX + 8, 122);

      sd(...RULE);
      doc.setLineWidth(0.3);
      doc.line(TX + 8, 124, TX + CW - 20, 124);

      const toc = [
        ['01', 'Competency Categories'],
        ['02', 'Core Competencies'],
        ['03', 'Technical Competencies'],
        ['04', 'Competency Descriptors'],
      ];
      let tocY = 132;
      toc.forEach(([num, label]) => {
        sf(...G_MID);
        doc.roundedRect(TX + 8, tocY - 4, 8, 6, 1, 1, 'F');
        st(...WHITE);
        doc.setFontSize(6.5);
        doc.setFont('helvetica', 'bold');
        doc.text(num, TX + 12, tocY, { align: 'center' });
        st(...DARK);
        doc.setFontSize(9.5);
        doc.setFont('helvetica', 'normal');
        doc.text(label, TX + 20, tocY);
        tocY += 11;
      });

      // Stats strip
      const statsData = [
        { n: categories.length, label: 'Categories' },
        { n: coreGroups.length, label: 'Core Groups' },
        { n: coreGroups.reduce((s, g) => s + (g.items?.length || 0), 0), label: 'Core Competencies' },
        { n: deptData.reduce((s, d) => s + d.groups.length, 0), label: 'Technical Groups' },
      ];
      const bw  = CW / statsData.length;
      const bY  = 172;
      statsData.forEach((s, i) => {
        const bx = ML + i * bw;
        sf(i % 2 === 0 ? 244 : 250, 250, 248);
        sd(...RULE);
        doc.setLineWidth(0.25);
        doc.roundedRect(bx + 1, bY, bw - 2, 22, 2, 2, 'FD');
        st(...G_DARK);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text(String(s.n), bx + bw / 2, bY + 11, { align: 'center' });
        st(...MUTED);
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'normal');
        doc.text(s.label, bx + bw / 2, bY + 18, { align: 'center' });
      });

      // ════════════════════════════════════════════════════════════════════════
      // SECTION 1 — Competency Categories
      // ════════════════════════════════════════════════════════════════════════
      let y = sectionPage(1, 'Competency Categories', 'Organisation-wide category weightings');

      doc.autoTable({
        startY: y,
        head: [['#', 'Category Name', 'Weighting']],
        body: categories.map((cat, i) => [
          `${String.fromCharCode(65 + i)}.`,
          cat.category_name,
          `${cat.category_weight}%`,
        ]),
        theme: 'striped',
        headStyles,
        bodyStyles,
        alternateRowStyles: altRowStyles,
        columnStyles: {
          0: { cellWidth: 14, halign: 'center', fontStyle: 'bold', textColor: G_DARK },
          2: { cellWidth: 26, halign: 'center', fontStyle: 'bold', textColor: G_DARK },
        },
        styles: tableStyles,
        margin: tableMargin,
        didDrawPage: () => runningHeader(),
      });

      // ════════════════════════════════════════════════════════════════════════
      // SECTION 2 — Core Competencies
      // ════════════════════════════════════════════════════════════════════════
      y = sectionPage(2, 'Core Competencies', 'Organisation-wide competency groups and competencies');

      doc.autoTable({
        startY: y,
        head: [['', 'Competency Group', 'Wt.', 'Competencies']],
        body: coreGroups.map((g, i) => [
          String.fromCharCode(65 + i),
          g.group_name + (g.description ? `\n${g.description}` : ''),
          `${g.weighting}%`,
          (g.items || []).map(c => c.competency_name).join('\n') || '—',
        ]),
        theme: 'striped',
        headStyles,
        bodyStyles,
        alternateRowStyles: altRowStyles,
        columnStyles: {
          0: { cellWidth: 10, halign: 'center', fontStyle: 'bold', textColor: G_DARK },
          1: { cellWidth: 90 },
          2: { cellWidth: 22, halign: 'center', fontStyle: 'bold', textColor: G_DARK },
          3: { cellWidth: CW - 10 - 90 - 22 },
        },
        styles: tableStyles,
        margin: tableMargin,
        didDrawCell: (data) => {
          if (data.section !== 'body' || data.column.index !== 1) return;
          const raw = String(data.cell.raw || '');
          const nl  = raw.indexOf('\n');
          if (nl === -1) return;
          const { x, y: cy, width, height } = data.cell;
          const rowBg = data.row.index % 2 === 0 ? WHITE : G_LIGHT;
          sf(...rowBg);
          doc.rect(x + 0.15, cy + 0.15, width - 0.3, height - 0.3, 'F');
          doc.setFontSize(8.5); doc.setFont('helvetica', 'bold'); st(...DARK);
          doc.text(raw.substring(0, nl), x + 6, cy + 7);
          doc.setFontSize(7.5); doc.setFont('helvetica', 'italic'); st(...MUTED);
          doc.text(doc.splitTextToSize(raw.substring(nl + 1), width - 12), x + 6, cy + 13);
          doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); st(...DARK);
        },
        didDrawPage: () => runningHeader(),
      });

      // ════════════════════════════════════════════════════════════════════════
      // SECTION 3 — Technical Competencies
      // ════════════════════════════════════════════════════════════════════════
      let techY = sectionPage(3, 'Technical Competencies', 'Department-specific competency groups');

      deptData.forEach((dept, di) => {
        if (techY > PH - 70 && di > 0) { doc.addPage(); runningHeader(); techY = 20; }
        techY = deptBar(dept.depart_name, techY);

        doc.autoTable({
          startY: techY,
          head: [['', 'Competency Group', 'Wt.', 'Competencies']],
          body: dept.groups.map((g, i) => [
            String.fromCharCode(65 + i),
            g.group_name + (g.description ? `\n${g.description}` : ''),
            `${g.group_weight}%`,
            (g.items || []).map(it => it.item_name).join('\n') || '—',
          ]),
          theme: 'striped',
          headStyles: { ...headStyles, fillColor: G_DARK },
          bodyStyles,
          alternateRowStyles: altRowStyles,
          columnStyles: {
            0: { cellWidth: 10, halign: 'center', fontStyle: 'bold', textColor: G_DARK },
            1: { cellWidth: 90 },
            2: { cellWidth: 22, halign: 'center', fontStyle: 'bold', textColor: G_DARK },
            3: { cellWidth: CW - 10 - 90 - 22 },
          },
          styles: tableStyles,
          margin: tableMargin,
          didDrawPage: () => runningHeader(),
        });
        techY = doc.lastAutoTable.finalY + 10;
      });

      // ════════════════════════════════════════════════════════════════════════
      // SECTION 4 — Competency Descriptors
      // ════════════════════════════════════════════════════════════════════════
      if (descriptorData.length > 0) {
        let descY = sectionPage(4, 'Competency Descriptors', 'Behavioural descriptors by career level');

        descriptorData.forEach((group, gi) => {
          if (descY > PH - 70 && gi > 0) { doc.addPage(); runningHeader(); descY = 20; }
          descY = groupBar(group.group_name, descY);

          doc.autoTable({
            startY: descY,
            head: [['Competency', ...levelLabels]],
            body: group.competencies.map(comp => [
              comp.competency_name,
              (comp.descriptors[1] || []).map(d => `• ${d}`).join('\n\n') || '—',
              (comp.descriptors[2] || []).map(d => `• ${d}`).join('\n\n') || '—',
              (comp.descriptors[3] || []).map(d => `• ${d}`).join('\n\n') || '—',
              (comp.descriptors[4] || []).map(d => `• ${d}`).join('\n\n') || '—',
            ]),
            theme: 'striped',
            headStyles: { ...headStyles, fontSize: 8 },
            bodyStyles: { ...bodyStyles, fontSize: 8 },
            alternateRowStyles: altRowStyles,
            columnStyles: {
              0: { cellWidth: 40, fontStyle: 'bold' },
              1: { cellWidth: (CW - 40) / 4 },
              2: { cellWidth: (CW - 40) / 4 },
              3: { cellWidth: (CW - 40) / 4 },
              4: { cellWidth: (CW - 40) / 4 },
            },
            styles: tableStyles,
            margin: tableMargin,
            didDrawPage: () => runningHeader(),
          });
          descY = doc.lastAutoTable.finalY + 10;
        });
      }

      // ── Footers on every page ─────────────────────────────────────────────
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        // Skip footer on cover (page 1)
        if (i === 1) {
          sf(...G_DARK);
          doc.rect(0, PH - 14, PW, 14, 'F');
          st(...WHITE);
          doc.setFontSize(7.5);
          doc.setFont('helvetica', 'bold');
          doc.text('TALENTSCOPE360', ML + 8, PH - 6);
          doc.setFont('helvetica', 'normal');
          st(180, 220, 205);
          doc.text('Competency Framework  ·  Confidential', ML + 44, PH - 6);
          continue;
        }
        sf(...G_DARK);
        doc.rect(0, PH - 10, PW, 10, 'F');
        st(...WHITE);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.text('TalentScope360  ·  Competency Framework  ·  Confidential', ML, PH - 4);
        doc.text(`Page ${i} of ${pageCount}`, PW - MR, PH - 4, { align: 'right' });
      }

      doc.save(`competency-framework-${new Date().toISOString().slice(0, 10)}.pdf`);
      setGenerating(false);
    }, 20);
  };


  // ── Derived totals ──────────────────────────────────────────────────────────
  const totalCoreComps  = coreGroups.reduce((s, g) => s + (g.items?.length || 0), 0);
  const totalTechGroups = deptData.reduce((s, d) => s + d.groups.length, 0);
  const totalTechComps  = deptData.reduce((s, d) => s + d.groups.reduce((gs, g) => gs + (g.items?.length || 0), 0), 0);

  const TABS = [
    { id: 'A', label: 'Competency Categories' },
    { id: 'B', label: 'Core Competencies' },
    { id: 'C', label: 'Technical Competencies' },
    { id: 'D', label: 'Competency Descriptors' },
  ];

  if (loading) {
    return <div className="cf-loading">Loading framework data…</div>;
  }

  return (
    <div className="cf-root">

      {/* ── Page header (title + subtitle only) ── */}
      <div className="cf-page-header">
        <h1 className="cf-title">
          <i className="bi bi-eye" /> Competency Model
        </h1>
        <p className="cf-subtitle">
          A consolidated view of your organisation's full competency model — categories,
          core and technical competencies, and behavioural descriptors.
        </p>
      </div>
      <hr className="cf-divider" />

      {/* ── Toolbar: download button sits below the divider ── */}
      <div className="cf-toolbar">
        <button
          className="cf-btn cf-btn-download"
          onClick={handleDownloadAll}
          disabled={generating}
        >
          <i className="bi bi-file-earmark-arrow-down" />
          {generating ? 'Generating…' : 'Download Model'}
        </button>
      </div>

      {/* ── Summary stats ── */}
      <div className="cf-stats-strip">
        {[
          { value: categories.length,  label: 'Categories' },
          { value: coreGroups.length,  label: 'Core Groups' },
          { value: totalCoreComps,     label: 'Core Competencies' },
          { value: totalTechGroups,    label: 'Technical Groups' },
          { value: totalTechComps,     label: 'Technical Competencies' },
        ].map(({ value, label }) => (
          <div key={label} className="cf-stat">
            <span className="cf-stat-value">{value}</span>
            <span className="cf-stat-label">{label}</span>
          </div>
        ))}
      </div>

      {/* ── Section tabs (A / B / C / D) ── */}
      <div className="cf-main-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`cf-main-tab ${activeTab === tab.id ? 'cf-main-tab-active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="cf-main-tab-badge">{tab.id}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── A: Competency Categories ── */}
      {activeTab === 'A' && (
        <section className="cf-section">
          <div className="cf-section-header">
            <div className="cf-section-title-row">
              <span className="cf-section-badge">A</span>
              <h2 className="cf-section-title">Competency Categories</h2>
            </div>
            <p className="cf-section-desc">
              Categories and their global weightings across the framework.
            </p>
          </div>
          {categories.length === 0 ? (
            <div className="cf-empty">No competency categories configured.</div>
          ) : (
            <table className="cf-table">
              <thead>
                <tr>
                  <th className="cf-th-num">#</th>
                  <th>Category</th>
                  <th className="cf-th-weight-col">Weighting</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat, i) => (
                  <tr key={cat.category_id}>
                    <td className="cf-td-index">{String.fromCharCode(65 + i)}.</td>
                    <td>{cat.category_name}</td>
                    <td className="cf-td-weight">{cat.category_weight}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {/* ── B: Core Competencies ── */}
      {activeTab === 'B' && (
        <section className="cf-section">
          <div className="cf-section-header">
            <div className="cf-section-title-row">
              <span className="cf-section-badge">B</span>
              <h2 className="cf-section-title">Core Competencies</h2>
            </div>
            <p className="cf-section-desc">
              Organisation-wide competency groups and their competencies.
            </p>
          </div>
          {coreGroups.length === 0 ? (
            <div className="cf-empty">No core competencies configured.</div>
          ) : (
            <table className="cf-table cf-table-fixed">
              <thead>
                <tr>
                  <th className="cf-th-num">#</th>
                  <th className="cf-th-group">Competency Group</th>
                  <th className="cf-th-weight-col">Weight</th>
                  <th>Competencies</th>
                </tr>
              </thead>
              <tbody>
                {coreGroups.map((group, i) => (
                  <tr key={group.group_id}>
                    <td className="cf-td-index">{String.fromCharCode(65 + i)}.</td>
                    <td>
                      <span className="cf-group-name">{group.group_name}</span>
                      {group.description && (
                        <p className="cf-group-desc">{group.description}</p>
                      )}
                    </td>
                    <td className="cf-td-weight">{group.weighting}%</td>
                    <td>
                      <ol className="cf-comp-list">
                        {(group.items || []).map(c => (
                          <li key={c.competency_id}>{c.competency_name}</li>
                        ))}
                      </ol>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {/* ── C: Technical Competencies ── */}
      {activeTab === 'C' && (
        <section className="cf-section">
          <div className="cf-section-header">
            <div className="cf-section-title-row">
              <span className="cf-section-badge">C</span>
              <h2 className="cf-section-title">Technical Competencies</h2>
            </div>
            <p className="cf-section-desc">
              Department-specific competency groups. All departments are included in the downloaded report.
            </p>
          </div>
          {deptData.length === 0 ? (
            <div className="cf-empty">No technical competencies configured.</div>
          ) : (
            <>
              <div className="cf-dept-tabs">
                {deptData.map(dept => (
                  <button
                    key={dept.department_id}
                    className={`cf-dept-tab ${selectedDeptId === dept.department_id ? 'cf-dept-tab-active' : ''}`}
                    onClick={() => setSelectedDeptId(dept.department_id)}
                  >
                    {dept.depart_name}
                  </button>
                ))}
              </div>
              {activeDept && (
                <table className="cf-table cf-table-fixed">
                  <thead>
                    <tr>
                      <th className="cf-th-num">#</th>
                      <th className="cf-th-group">Competency Group</th>
                      <th className="cf-th-weight-col">Weight</th>
                      <th>Competencies</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeDept.groups.map((group, i) => (
                      <tr key={group.group_id}>
                        <td className="cf-td-index">{String.fromCharCode(65 + i)}.</td>
                        <td>
                          <span className="cf-group-name">{group.group_name}</span>
                          {group.description && (
                            <p className="cf-group-desc">{group.description}</p>
                          )}
                        </td>
                        <td className="cf-td-weight">{group.group_weight}%</td>
                        <td>
                          <ol className="cf-comp-list">
                            {(group.items || []).map(it => (
                              <li key={it.item_id}>{it.item_name}</li>
                            ))}
                          </ol>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </section>
      )}

      {/* ── D: Competency Descriptors ── */}
      {activeTab === 'D' && (
        <section className="cf-section">
          <div className="cf-section-header">
            <div className="cf-section-title-row">
              <span className="cf-section-badge">D</span>
              <h2 className="cf-section-title">Competency Descriptors</h2>
            </div>
            <p className="cf-section-desc">
              Behavioural descriptors for core and functional competencies across the four career levels.
            </p>
          </div>
          {descriptorData.length === 0 && funcDescriptorData.length === 0 ? (
            <div className="cf-empty">No competency descriptors configured.</div>
          ) : (
            <>
              {/* Core competency descriptors */}
              {descriptorData.length > 0 && (
                <>
                  {funcDescriptorData.length > 0 && (
                    <div className="cf-desc-section-label">Core Competencies</div>
                  )}
                  {descriptorData.map(group => (
                    <div key={group.group_id} className="cf-desc-group">
                      <div className="cf-desc-group-title">{group.group_name}</div>
                      <div className="cf-desc-table-wrap">
                        <table className="cf-table cf-desc-table">
                          <thead>
                            <tr>
                              <th className="cf-desc-th-comp">Competency</th>
                              {levelLabels.map(l => <th key={l}>{l}</th>)}
                            </tr>
                          </thead>
                          <tbody>
                            {group.competencies.map(comp => (
                              <tr key={comp.competency_id}>
                                <td className="cf-desc-comp-name">{comp.competency_name}</td>
                                {[1, 2, 3, 4].map(lv => (
                                  <td key={lv} className="cf-desc-cell">
                                    {(comp.descriptors[lv] || []).length > 0 ? (
                                      <ul className="cf-desc-list">
                                        {comp.descriptors[lv].map((d, i) => <li key={i}>{d}</li>)}
                                      </ul>
                                    ) : (
                                      <span className="cf-desc-empty">—</span>
                                    )}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </>
              )}
              {/* Functional competency descriptors */}
              {funcDescriptorData.length > 0 && (
                <>
                  <div className="cf-desc-section-label">Functional Competencies</div>
                  {funcDescriptorData.map(dept => (
                    <div key={dept.department_id}>
                      <div className="cf-desc-dept-label">{dept.depart_name}</div>
                      {dept.groups.map(group => (
                        <div key={group.group_id} className="cf-desc-group">
                          <div className="cf-desc-group-title">{group.group_name}</div>
                          <div className="cf-desc-table-wrap">
                            <table className="cf-table cf-desc-table">
                              <thead>
                                <tr>
                                  <th className="cf-desc-th-comp">Competency</th>
                                  {levelLabels.map(l => <th key={l}>{l}</th>)}
                                </tr>
                              </thead>
                              <tbody>
                                {group.competencies.map(comp => (
                                  <tr key={comp.item_id}>
                                    <td className="cf-desc-comp-name">{comp.item_name}</td>
                                    {[1, 2, 3, 4].map(lv => (
                                      <td key={lv} className="cf-desc-cell">
                                        {(comp.descriptors[lv] || []).length > 0 ? (
                                          <ul className="cf-desc-list">
                                            {comp.descriptors[lv].map((d, i) => <li key={i}>{d}</li>)}
                                          </ul>
                                        ) : (
                                          <span className="cf-desc-empty">—</span>
                                        )}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </section>
      )}

    </div>
  );
};

export default CompetencyFramework;
